// components/socials/PostDetail.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  FiBookmark,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiHeart,
  FiMaximize,
  FiMinimize,
  FiMoreVertical,
  FiMessageCircle,
  FiShare2,
  FiTrash2,
} from "react-icons/fi";
import firebase from "firebase/app";
import "firebase/firestore";

import { db } from "../../firebase/firebase.utils";
import Avatar from "../retro/avatar/avatar.component";
import PostComments from "./PostComments";
import FrameSurface from "./frame-editor/FrameSurface";
import PostPlayback from "./PostPlayback";
import "./post-detail.styles.scss";
import { frameCore } from "./frame-editor/frameCore";
import Report from "../report/Report";
import { createReport } from "../report/createReport";
import {
  bumpPostSignals,
  togglePostReaction,
  usePostReactions,
} from "./postReactions";

const TOP_PADDING = 10;
const BOTTOM_PADDING = 10;

const EMPTY_ASSETS = Object.freeze({});

// Keep each nearby carousel page's DOM/media node stable while it is prepared.
function MediaPage({
  item,
  prepared,
  active,
  assets,
  fit,
  preload,
  bind,
  onPlayback,
}) {
  const scene = useMemo(() => item.scene || frameCore.fromMedia(item), [item]);
  const poster = frameCore.previewURL(item, assets);
  const identity = scene.id + "|" + scene.background.url + "|" + poster;
  const [painted, setPainted] = useState(null);
  return (
    <div className="post-detail__media-page">
      {prepared && (
        <FrameSurface
          ref={bind}
          assets={assets}
          controls={false}
          frame={scene}
          active={active}
          fit={fit}
          preload={preload}
          onPlayback={onPlayback}
          onMediaReady={() => setPainted(identity)}
          style={{
            position: "absolute",
            inset: 0,
          }}
        />
      )}
      {painted !== identity && (
        <div className="post-detail__loading-cover" aria-hidden="true">
          {poster && (
            <img
              className="post-detail__poster"
              src={poster}
              alt=""
              style={{
                objectFit:
                  fit === "cover-tall" && scene.width / scene.height === 9 / 16
                    ? "cover"
                    : "contain",
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

const formatCount = (value) => {
  const count = Number(value || 0);
  if (count < 1000) return String(count);
  if (count < 1000000)
    return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k`;
  return `${(count / 1000000).toFixed(count >= 10000000 ? 0 : 1)}m`;
};

export const timeAgo = (timestamp) => {
  const date =
    timestamp?.toDate?.() || (timestamp instanceof Date ? timestamp : null);
  if (!date) return "";

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)}w`;
  if (seconds < 31536000) return `${Math.floor(seconds / 2592000)}mo`;
  return `${Math.floor(seconds / 31536000)}y`;
};

const normalizeMedia = (post) => {
  const media = Array.isArray(post?.media) ? post.media.filter(Boolean) : [];

  if (!media.length) {
    const url = post?.coverMediaUrl || post?.mediaUrl;
    return url ? [{ id: "cover", kind: "image", url }] : [];
  }

  return media.map((item, index) => ({
    scene: item.scene || null,
    id: item.id || `${item.kind || item.type || "media"}_${index}`,
    kind: item.kind || item.type || "image",
    url: item.url || item.mediaUrl || null,
    coverUrl: item.coverUrl || null,
    durationMs: typeof item.durationMs === "number" ? item.durationMs : null,
    html: item.html || "",
    backgroundUrl: item.backgroundUrl || null,
    bgColor: item.bgColor || "#fff",
    bgResizeMode: item.bgResizeMode || "cover",
    hAlign: item.hAlign || "left",
    vAlign: item.vAlign || "top",
    textColor: item.textColor || "#111",
    textFont: item.textFont || "system-ui, sans-serif",
  }));
};

export default function PostDetail({
  postId,
  initialPost = null,
  initialAuthor = null,
  active = true,
  preview = false,
  draftAssets = EMPTY_ASSETS,
  inFeed = false,
  prepareMedia = active,
  preloadMedia = "metadata",
  onPostUpdated,
  fullscreen = false,
  profileViewer = false,
  onToggleFullscreen,
  onExitProfileViewer,
  onOpenAuthor,
  onDeleted,
}) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const uid = currentUser?.uid;

  const [post, setPost] = useState(initialPost);
  const [author, setAuthor] = useState(initialAuthor);
  const seed = useRef(null),
    authorLoads = useRef(new Map());
  seed.current = {
    initialPost,
    initialAuthor,
    author,
    post,
    onPostUpdated: onPostUpdated,
  };

  const [loading, setLoading] = useState(!initialPost);
  const [page, setPage] = useState(0);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [following, setFollowing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState("");
  const seenTimer = useRef(null);
  const sentSignals = useRef(new Set());

  const [reportTarget, setReportTarget] = useState(null);

  const openReport = () => {
    setReportTarget({
      targetType: "post",
      targetId: postId,
      targetPath: `posts/${postId}`,
      reportedUserId: post.authorId,
      messageText: [post.text, ...normalizeMedia(post).map((m) => m.html)]
        .filter(Boolean)
        .join("\n\n"),
      messageCreatedAt: post.createdAt || null,
      attachments: normalizeMedia(post).map((m) => ({
        kind: m.kind,
        url: m.url || m.backgroundUrl || "",
        thumbUrl: m.coverUrl || "",
      })),
    });
  };

  useEffect(() => {
    setPage(0);
    setCaptionOpen(false);
    setCommentsOpen(false);
    sentSignals.current.clear();
  }, [postId]);

  useEffect(() => {
    if (preview) {
      setPost(initialPost);
      setAuthor(initialAuthor);
      setLoading(false);
    } else if (initialPost && initialPost.id === postId) {
      setPost(initialPost);
      setLoading(false);
      if (initialAuthor) setAuthor(initialAuthor);
    }
  }, [initialPost, initialAuthor, preview, postId]);

  useEffect(() => {
    if (preview || !postId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const known = seed.current;
    if (known.post?.id !== postId && known.initialPost?.id !== postId) {
      setPost(null);
      setAuthor(null);
      setLoading(true);
    }
    const failed = (error) => {
      if (!cancelled) {
        console.log("Post load error:", error);
        setLoading(false);
      }
    };
    const applyPost = async (snapshot) => {
      if (cancelled) return;
      if (!snapshot.exists) {
        setPost(null);
        setAuthor(null);
        setLoading(false);
        return;
      }
      const next = { id: snapshot.id, ...snapshot.data() };
      setPost(next);
      setLoading(false);
      seed.current.onPostUpdated?.(next);
      const authorId = next.authorId;
      if (!authorId) {
        setAuthor(null);
        return;
      }
      const cached = [seed.current.initialAuthor, seed.current.author].find(
        (a) => a && (a.id === authorId || a.uid === authorId),
      );
      if (cached) {
        setAuthor(cached);
        return;
      }
      try {
        if (!authorLoads.current.has(authorId))
          authorLoads.current.set(
            authorId,
            db
              .collection("users")
              .doc(authorId)
              .get()
              .then((snapshot) =>
                snapshot.exists
                  ? { id: snapshot.id, ...snapshot.data() }
                  : null,
              )
              .catch((error) => {
                authorLoads.current.delete(authorId);
                throw error;
              }),
          );
        const value = await authorLoads.current.get(authorId);
        if (
          !cancelled &&
          (!seed.current.post?.authorId ||
            seed.current.post.authorId === authorId)
        )
          setAuthor(value);
      } catch (error) {
        if (!cancelled) console.log("Post author load error:", error);
      }
    };
    // Neighbor preparation uses the data already paid for by the feed query.
    if (!active) {
      if (known.initialPost?.id === postId || known.post?.id === postId) return;
      db.collection("posts").doc(postId).get().then(applyPost).catch(failed);
      return () => {
        cancelled = true;
      };
    }
    const unsubscribe = db
      .collection("posts")
      .doc(postId)
      .onSnapshot(applyPost, failed);
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [postId, active, preview]);

  useEffect(() => {
    if (preview || !active || !uid || !post?.authorId || post.authorId === uid)
      return undefined;

    const followId = `${uid}_${post.authorId}`;
    return db
      .collection("follows")
      .doc(followId)
      .onSnapshot(
        (snapshot) => setFollowing(snapshot.exists),
        (error) => console.log("Follow snapshot error:", error),
      );
  }, [preview, active, uid, post?.authorId]);

  useEffect(() => {
    if (preview || !active || !uid || !postId) return undefined;

    return db
      .collection("users")
      .doc(uid)
      .collection("savedPosts")
      .doc(postId)
      .onSnapshot(
        (snapshot) => setSaved(snapshot.exists),
        (error) => console.log("Saved post snapshot error:", error),
      );
  }, [preview, active, uid, postId]);

  useEffect(() => {
    if (post?.moderationHidden || preview || !active || !uid || !post?.id || post.authorId === uid)
      return undefined;

    seenTimer.current = window.setTimeout(() => {
      if (!sentSignals.current.has("seen")) {
        sentSignals.current.add("seen");
        bumpPostSignals(uid, post, "seen").catch((error) =>
          console.log("Seen signal error:", error),
        );
      }
    }, 1400);

    return () => window.clearTimeout(seenTimer.current);
  }, [preview, active, uid, post]);

  const {
    groups,
    myReaction,
    total: liveReactionCount,
  } = usePostReactions(postId, uid, active && !preview);

  const media = useMemo(() => normalizeMedia(post), [post]);
  const currentMedia = media[page];
  const scene = useMemo(
    () =>
      currentMedia && (currentMedia.scene || frameCore.fromMedia(currentMedia)),
    [currentMedia],
  );
  const surfaces = useRef(new Map()),
    playback = useRef(null);
  const mediaKey = currentMedia
    ? `${postId || "preview"}:${currentMedia.id}`
    : null;
  const fit =
    (inFeed || preview) && !fullscreen && !profileViewer
      ? "cover-tall"
      : "contain";
  const isVideo = scene?.background?.kind === "video";
  const isOwner = !!uid && post?.authorId === uid;

  const sendSignalOnce = async (kind) => {
    if (sentSignals.current.has(kind)) return;
    sentSignals.current.add(kind);
    try {
      await bumpPostSignals(uid, post, kind);
    } catch (error) {
      console.log(`${kind} signal error:`, error);
    }
  };

  const toggleLike = async () => {
    if (!uid || busy) return;
    try {
      setBusy("like");
      await togglePostReaction(postId, uid, "❤️");
      if (myReaction?.emoji !== "❤️") await sendSignalOnce("like");
    } catch (error) {
      console.log("Like error:", error);
    } finally {
      setBusy("");
    }
  };

  const toggleFollow = async () => {
    if (!uid || !post?.authorId || isOwner || busy) return;
    const ref = db.collection("follows").doc(`${uid}_${post.authorId}`);

    try {
      setBusy("follow");
      if (following) {
        await ref.delete();
      } else {
        await ref.set(
          {
            followerId: uid,
            followingId: post.authorId,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true },
        );
        await sendSignalOnce("follow");
      }
    } catch (error) {
      console.log("Follow error:", error);
    } finally {
      setBusy("");
    }
  };

  const toggleSave = async () => {
    if (!uid || !postId || busy) return;
    const ref = db
      .collection("users")
      .doc(uid)
      .collection("savedPosts")
      .doc(postId);

    try {
      setBusy("save");
      if (saved) {
        await ref.delete();
      } else {
        await ref.set(
          {
            postId,
            authorId: post.authorId || "",
            type: post.type || "",
            tags: (post.tags || []).slice(0, 20),
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true },
        );
        await sendSignalOnce("save");
      }
    } catch (error) {
      console.log("Save error:", error);
    } finally {
      setBusy("");
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/post/${postId}`;
    try {
      await navigator.clipboard.writeText(url);
      window.alert("Post link copied.");
    } catch (error) {
      console.log("Share error:", error);
      window.prompt("Copy this post link:", url);
    }
  };

  const togglePin = async () => {
    if (!isOwner) return;
    try {
      await db.collection("posts").doc(postId).update({
        isPinned: !post.isPinned,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    } catch (error) {
      console.log("Pin error:", error);
    }
  };

  const removePost = async () => {
    if (!isOwner || !window.confirm("Delete this post? This cannot be undone."))
      return;
    try {
      await db.collection("posts").doc(postId).delete();
      onDeleted?.(postId);
    } catch (error) {
      console.log("Post delete error:", error);
      window.alert("The post could not be deleted.");
    }
  };

  if (loading)
    return <div className="post-detail post-detail--center">Loading…</div>;
  if (post?.moderationHidden === true)
    return <div className="post-detail post-detail--center" role="status">This post was removed by moderation.</div>;
  if (!post)
    return (
      <div className="post-detail post-detail--center">Post not found.</div>
    );

  const reactionCount = Math.max(
    Number(post.likeCount || 0),
    liveReactionCount,
  );

  const captionTags = (post.tags || []).filter(
    (tag) => typeof tag === "string" && tag.trim(),
  );
  const hasCaption = Boolean(
    post.text?.trim() || captionTags.length || (!preview && reactionCount > 0),
  );
  const captionExpanded = hasCaption && captionOpen;

  return (
    <article
      style={{
        "--post-frame-top": `${TOP_PADDING}px`,
        "--post-frame-bottom": `${BOTTOM_PADDING}px`,
      }}
      className={`post-detail${fullscreen ? " post-detail--fullscreen" : ""}${media.length ? " post-detail--has-media" : ""}${preview ? " post-detail--preview" : ""}`}
    >
      <header
        className={`post-detail__header ${
          profileViewer ? "post-detail__header--profile-viewer" : ""
        }`}
      >
        {profileViewer ? (
          <button
            className="post-detail__profile-back"
            type="button"
            onClick={onExitProfileViewer}
            aria-label="Back to all posts"
          >
            <FiChevronLeft />
          </button>
        ) : (
          <>
            <button
              className="post-detail__author"
              type="button"
              disabled={preview}
              onClick={() => onOpenAuthor?.(post.authorId)}
            >
              <Avatar
                user={author}
                src={
                  (Array.isArray(author?.imageUrl)
                    ? author.imageUrl.find(Boolean)
                    : author?.imageUrl) ||
                  author?.imgUrl ||
                  author?.photoURL
                }
                size={30}
              />
              <span>
                <strong>{author?.userName || "User"}</strong>
                <small>
                  {post.type} · {post.visibility}
                  {preview
                    ? " · Draft"
                    : timeAgo(post.createdAt)
                      ? ` · ${timeAgo(post.createdAt)}`
                      : ""}
                </small>
              </span>
            </button>

            {!preview && !isOwner && uid && (
              <button
                className="post-detail__follow"
                type="button"
                disabled={busy === "follow"}
                onClick={toggleFollow}
              >
                {busy === "follow" ? "…" : following ? "Following" : "Follow"}
              </button>
            )}

            {(preview || onToggleFullscreen) && (
              <button
                className="post-detail__icon-button"
                type="button"
                onClick={onToggleFullscreen}
                disabled={preview && !onToggleFullscreen}
                title={
                  preview && !onToggleFullscreen
                    ? "Fullscreen is available after publishing"
                    : undefined
                }
                aria-label={fullscreen ? "Exit fullscreen" : "Open fullscreen"}
              >
                {fullscreen ? <FiMinimize /> : <FiMaximize />}
              </button>
            )}
          </>
        )}

        {preview ? (
          <button
            type="button"
            className="post-detail__icon-button"
            disabled
            aria-label="Post menu"
            title="Post actions are available after publishing"
          >
            <FiMoreVertical />
          </button>
        ) : (
          <details className="post-detail__menu">
            <summary aria-label="Post menu">
              <FiMoreVertical />
            </summary>
            <div>
              {!!uid && !isOwner && !!post.authorId && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.currentTarget.closest("details").open = false;
                    openReport();
                  }}
                >
                  Report
                </button>
              )}
              {isOwner && (
                <button type="button" onClick={togglePin}>
                  {post.isPinned ? "Unpin from profile" : "Pin on profile"}
                </button>
              )}
              {isOwner && (
                <button className="danger" type="button" onClick={removePost}>
                  <FiTrash2 /> Delete post
                </button>
              )}
            </div>
          </details>
        )}
      </header>

      <div
        className={`post-detail__content ${media.length ? "has-media" : "no-media"}`}
        style={
          currentMedia
            ? { backgroundColor: currentMedia.bgColor || "#ffffff" }
            : undefined
        }
      >
        {currentMedia ? (
          <>
            {media.map((item, index) => {
              const prepared =
                (prepareMedia && index === page) ||
                (active && Math.abs(index - page) <= 1);
              if (!prepared) return null;
              return (
                <div
                  key={item.id}
                  aria-hidden={index !== page}
                  style={{
                    position: "absolute",
                    inset: 0,
                    visibility: index === page ? "visible" : "hidden",
                    pointerEvents: index === page ? "auto" : "none",
                  }}
                >
                  <MediaPage
                    item={item}
                    prepared
                    active={active && index === page}
                    assets={preview ? draftAssets : EMPTY_ASSETS}
                    fit={fit}
                    preload={index === page ? preloadMedia : "metadata"}
                    bind={(node) => {
                      if (node) surfaces.current.set(item.id, node);
                      else surfaces.current.delete(item.id);
                    }}
                    onPlayback={(state) => {
                      if (index === page) playback.current?.update(state);
                    }}
                  />
                </div>
              );
            })}

            {media.length > 1 && (
              <>
                <button
                  className="post-detail__slide-button previous"
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((value) => Math.max(0, value - 1))}
                  aria-label="Previous frame"
                >
                  <FiChevronLeft />
                </button>
                <button
                  className="post-detail__slide-button next"
                  type="button"
                  disabled={page === media.length - 1}
                  onClick={() =>
                    setPage((value) => Math.min(media.length - 1, value + 1))
                  }
                  aria-label="Next frame"
                >
                  <FiChevronRight />
                </button>
                <div className="post-detail__dots">
                  {media.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      className={index === page ? "active" : ""}
                      onClick={() => setPage(index)}
                      aria-label={`Open frame ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}

            <PostPlayback
              key={mediaKey}
              ref={playback}
              enabled={active && isVideo}
              onCommand={(command, data) =>
                surfaces.current.get(currentMedia?.id)?.command(command, data)
              }
            >
              {hasCaption && (
                <button
                  className={`post-detail__caption ${captionExpanded ? "open" : ""}`}
                  aria-expanded={captionExpanded}
                  type="button"
                  onClick={() => setCaptionOpen((value) => !value)}
                >
                  <span>
                    {post.text}
                    {!post.text?.trim() &&
                      !captionTags.length &&
                      reactionCount > 0 &&
                      `${formatCount(reactionCount)} ${reactionCount === 1 ? "reaction" : "reactions"}`}
                    {!captionExpanded &&
                      captionTags.map((tag) => ` #${tag}`).join("")}
                  </span>
                  {captionExpanded ? <FiChevronDown /> : <FiChevronUp />}

                  {captionExpanded && !!captionTags.length && (
                    <div className="post-detail__tags">
                      {captionTags.slice(0, 10).map((tag) => (
                        <em key={tag}>#{tag}</em>
                      ))}
                    </div>
                  )}

                  {captionExpanded && !!groups.length && (
                    <div className="post-detail__reaction-summary">
                      {groups
                        .slice(0, 3)
                        .map(({ emoji }) => emoji)
                        .join(" ")}{" "}
                      {formatCount(liveReactionCount)}
                    </div>
                  )}
                </button>
              )}
            </PostPlayback>
          </>
        ) : (
          <div className="post-detail__text-post">
            <p>{post.text}</p>
            {!!post.tags?.length && (
              <div className="post-detail__tags">
                {captionTags.slice(0, 10).map((tag) => (
                  <em key={tag}>#{tag}</em>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!preview && commentsOpen && (
        <PostComments
          post={post}
          currentUser={currentUser}
          onClose={() => setCommentsOpen(false)}
        />
      )}

      {!commentsOpen && !captionExpanded && (
        <nav className="post-detail__rail" aria-label="Post actions">
          <button
            type="button"
            onClick={toggleLike}
            aria-label="Like post"
            disabled={preview || !uid || busy === "like"}
          >
            <FiHeart className={myReaction?.emoji === "❤️" ? "filled" : ""} />
            <span>{formatCount(reactionCount)}</span>
          </button>
          <button
            type="button"
            disabled={preview}
            aria-label="Comments"
            onClick={() => {
              setCommentsOpen(true);
              sendSignalOnce("comment_open");
            }}
          >
            <FiMessageCircle />
            <span>{formatCount(post.commentCount)}</span>
          </button>
          <button
            type="button"
            disabled={preview}
            onClick={share}
            aria-label="Share post"
          >
            <FiShare2 />
          </button>
          <button
            type="button"
            onClick={toggleSave}
            aria-label="Save post"
            disabled={preview || !uid || busy === "save"}
          >
            <FiBookmark className={saved ? "filled" : ""} />
            <span>{busy === "save" ? "…" : formatCount(post.saveCount)}</span>
          </button>
        </nav>
      )}
      {reportTarget && (
        <Report
          reportTitle="Report post"
          onClose={() => setReportTarget(null)}
          onSubmit={(reason, details) =>
            createReport(uid, reportTarget, reason, details)
          }
        />
      )}
    </article>
  );
}
